import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import type { Message, MessageReplyTo } from "@/types/models"
import ReactMarkdown from 'react-markdown'
import { motion, AnimatePresence } from 'framer-motion'
import { Undo2, Copy, Check, Reply } from 'lucide-react'
import {
  toggleMessageLike,
  deleteMessageFromChat,
  buildMessageReplyToSnapshot,
} from "@/lib/chatData"
import { BookingSelectionMessage } from "@/components/chat/booking/BookingSelectionMessage"
import { PastBookingMessage } from "@/components/chat/booking/PastBookingMessage"
import { SimpleAvailabilityMessage } from "@/components/chat/booking/SimpleAvailabilityMessage"
import { ImageContextMenu } from "@/components/chat/ImageContextMenu"
import { ChatImageWithLoadingState } from '@/components/chat/ChatImageWithLoadingState'
import { ChatMessageSwipeReplyRow } from '@/components/chat/ChatMessageSwipeReplyRow'
import { useLongPress } from "@/hooks/useLongPress"
import { useDeferredImageTapOpen } from '@/hooks/useDeferredImageTapOpen'
import { useChatImagePreview } from '@/components/chat/ChatImagePreviewContext'
import { getFlatImageUrlsFromMessage, getMessageBubbleCornerClassName, getInlineReplyQuoteCornerClassName } from '@/lib/chatImageBurst'
import { scrollToChatMessageByIdAndFlash } from '@/lib/chatScrollToMessage'
import { useEventById } from "@/hooks/useEventById"
import { cn } from '@/lib/utils'
import { useAnalytics } from '@/hooks/useAnalytics'

const NON_COPYABLE_TYPES: Message['type'][] = [
  'notification',
  'booking_selection',
  'past_booking',
  'simple_availability',
]

function getMessageCopyText(message: Message): string | null {
  if (NON_COPYABLE_TYPES.includes(message.type)) return null
  if (message.type === 'text') {
    const trimmed = (message.content || '').trim()
    return trimmed.length > 0 ? message.content : null
  }
  if (message.type === 'image') {
    const parts: string[] = []
    const caption = (message.content || '').trim()
    if (caption) parts.push(caption)
    if (message.imageUrl) parts.push(message.imageUrl)
    if (message.imageUrls?.length) parts.push(...message.imageUrls)
    return parts.length > 0 ? parts.join('\n\n') : null
  }
  return null
}

interface ChatMessageProps {
  message: Message
  isOwnMessage: boolean
  isContinuation?: boolean
  isLastInGroup?: boolean
  isLastInChat?: boolean
  senderName?: string
  senderAvatar?: string
  senderFallbackInitial?: string
  chatId: string
  artistId?: string
  onBookingCreated?: () => void
  isArtist?: boolean
  onAddToTimeline?: (imageUrl: string) => void
  onRetryMessage?: (clientGeneratedId: string) => void
  onReplyToMessage?: (message: Message) => void
  getReplyQuoteAuthorLabel?: (reply: MessageReplyTo) => string
}

export function ChatMessage({ 
  message, 
  isOwnMessage, 
  isContinuation = false,
  isLastInGroup = false,
  isLastInChat = false,
  senderName,
  senderAvatar,
  senderFallbackInitial,
  chatId,
  artistId,
  onBookingCreated,
  isArtist = false,
  onAddToTimeline,
  onRetryMessage,
  onReplyToMessage,
  getReplyQuoteAuthorLabel,
}: ChatMessageProps) {
  const [justLiked, setJustLiked] = useState(false)
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; imageUrl: string } | null>(null)
  const [showActionOverlay, setShowActionOverlay] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [copyFeedback, setCopyFeedback] = useState<'idle' | 'copied'>('idle')
  const copyFeedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [messagePosition, setMessagePosition] = useState({ top: 0, left: 0, width: 0, height: 0 })
  const [messageSnapshot, setMessageSnapshot] = useState<{ html: string; className: string } | null>(null)
  const messageRef = useRef<HTMLDivElement>(null)
  const { trackEvent } = useAnalytics()
  const { event: eventData } = useEventById(message.metadata?.eventId)

  // Update message position and capture snapshot when overlay is shown
  useEffect(() => {
    if (showActionOverlay && messageRef.current) {
      const rect = messageRef.current.getBoundingClientRect()
      setMessagePosition({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      })
      // Capture the message HTML and classes for rendering above blur
      setMessageSnapshot({
        html: messageRef.current.innerHTML,
        className: messageRef.current.className
      })
    }
  }, [showActionOverlay])

  useEffect(() => {
    if (showActionOverlay) setCopyFeedback('idle')
  }, [showActionOverlay])

  useEffect(() => {
    return () => {
      if (copyFeedbackTimeoutRef.current) clearTimeout(copyFeedbackTimeoutRef.current)
    }
  }, [])

  // Timestamps are now shown as centered separators in the list, not per bubble

  // Handle notification messages
  if (message.type === 'notification') {
    return (
      <div className="flex justify-center my-4 w-full">
        <div className="text-sm text-gray-500 text-center max-w-xs">
          {message.content}
        </div>
      </div>
    )
  }

  // Handle booking selection messages (full booking system)
  if (message.type === 'booking_selection' && message.bookingData) {
    return (
      <div id={`booking-selection-message-${message.id}`} className="booking-selection-message">
        <BookingSelectionMessage
          estimatedTime={message.bookingData.estimatedTime}
          earliestDate={message.bookingData.earliestDate}
          preparationDays={message.bookingData.preparationDays}
          senderName={senderName}
          isOwnMessage={isOwnMessage}
          artistId={artistId}
          chatId={chatId}
          messageId={message.id}
          onBookingCreated={onBookingCreated}
        />
      </div>
    )
  }

  // Handle simple availability messages (non-booking system artists)
  if (message.type === 'simple_availability' && message.simpleBookingData) {
    return (
      <div id={`simple-availability-message-${message.id}`} className="simple-availability-message">
        <SimpleAvailabilityMessage
          priceRangeMin={message.simpleBookingData.priceRangeMin}
          priceRangeMax={message.simpleBookingData.priceRangeMax}
          availableDates={message.simpleBookingData.availableDates}
          estimatedTime={message.simpleBookingData.estimatedTime}
          senderName={senderName}
          isOwnMessage={isOwnMessage}
          messageId={message.id}
          chatId={chatId}
          artistId={artistId}
          onBookingCreated={onBookingCreated}
          bookingTime={message.simpleBookingData.bookingTime}
          isConfirmed={message.simpleBookingData.isConfirmed}
          availableTimeSlots={message.simpleBookingData.availableTimeSlots}
        />
      </div>
    )
  }

  // Handle past booking messages
  if (message.type === 'past_booking' && message.bookingData) {
    return (
      <div id="past-booking-message">
        <PastBookingMessage
          estimatedTime={message.bookingData.estimatedTime}
          earliestDate={message.bookingData.earliestDate}
        />
      </div>
    )
  }

  // Message container alignment
  const containerClass = isOwnMessage ? 'justify-end' : 'justify-start'
  
  const handleDoubleClick = async (e: React.MouseEvent) => {
    // Prevent text selection on double-click
    e.preventDefault()
    
    // Don't allow liking notification or special message types
    if (message.type === 'notification' || message.type === 'booking_selection' || message.type === 'past_booking' || message.type === 'simple_availability') {
      return
    }
    
    // Don't allow liking own messages
    if (isOwnMessage) {
      return
    }
    
    try {
      // Trigger animation for visual feedback
      if (!message.isLiked) {
        setJustLiked(true)
        setTimeout(() => setJustLiked(false), 400)
      }
      
      await toggleMessageLike(chatId, message.id)
    } catch (error) {
      console.error('Error toggling message like:', error)
    }
  }

  const copyText = getMessageCopyText(message)
  const canCopy = copyText !== null
  const canUnsend =
    isOwnMessage &&
    (message.type === 'text' || message.type === 'image')
  const canReply = buildMessageReplyToSnapshot(message) !== null

  const handleLongPressForActions = () => {
    if (canUnsend || canCopy || canReply) setShowActionOverlay(true)
  }

  const handleCopyPress = async () => {
    if (!copyText) return
    try {
      await navigator.clipboard.writeText(copyText)
      setCopyFeedback('copied')
      if (copyFeedbackTimeoutRef.current) clearTimeout(copyFeedbackTimeoutRef.current)
      copyFeedbackTimeoutRef.current = setTimeout(() => {
        setCopyFeedback('idle')
        copyFeedbackTimeoutRef.current = null
      }, 1000)
    } catch (err) {
      console.error('Copy failed:', err)
    }
  }

  const handleDelete = async () => {
    if (!message.id || !chatId) return
    
    setIsDeleting(true)
    try {
      await deleteMessageFromChat(chatId, message.id)
      await trackEvent('chat_message_deleted', {
        chat_id: chatId,
        role: isArtist ? 'artist' : 'user',
        message_id: message.id
      })
      // Message will be removed automatically via Firestore listener
    } catch (error) {
      console.error('Failed to delete message:', error)
      setIsDeleting(false)
      setShowActionOverlay(false)
    }
  }

  const handleDismissActionOverlay = () => {
    setShowActionOverlay(false)
  }

  const messageActionsLongPress = useLongPress({
    onLongPress: handleLongPressForActions,
    ms: 500,
  })

  const shouldReserveSpaceBelowLikedMessageForHeartPillOverhang =
    Boolean(message.isLiked) && !isLastInGroup

  const bubbleCornerClassName = getMessageBubbleCornerClassName(isContinuation, isOwnMessage)

  const handleConfirmSwipeReply = () => {
    onReplyToMessage?.(message)
  }

  return (
    <>
      <ChatMessageSwipeReplyRow
        swipeReplyEnabled={Boolean(!isOwnMessage && canReply && onReplyToMessage)}
        onSwipeConfirmReply={handleConfirmSwipeReply}
      >
      <div
        id={`chat-message-${message.id}`}
        className={cn(
          'flex w-full',
          containerClass,
          shouldReserveSpaceBelowLikedMessageForHeartPillOverhang
            ? 'mb-[calc(0.5rem+8px)]'
            : 'mb-2'
        )}
      >
        <div 
          className={`flex ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'} items-end gap-2 w-full`}
        >
          {/* Avatar column for non-own messages: keeps constant offset */}
          {!isOwnMessage && (
            <div className="w-8 h-8 flex-shrink-0">
              {isLastInGroup && (
                senderAvatar ? (
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-muted">
                    <img 
                      src={senderAvatar} 
                      alt={senderName || 'Avatar'} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-foreground text-xs font-semibold">
                      {(senderFallbackInitial || '?').toUpperCase()}
                    </span>
                  </div>
                )
              )}
            </div>
          )}
          
          {/* Message content */}
          <div className={`flex flex-col ${isOwnMessage ? 'items-end' : 'items-start'} max-w-[70%] min-w-0`}>
            {/* Message bubble with conditional bottom radius */}
            <motion.div 
              ref={messageRef}
              data-chat-scroll-anchor-bubble
              onDoubleClick={handleDoubleClick}
              {...(canUnsend || canCopy || canReply ? messageActionsLongPress : {})}
              animate={{ 
                scale: 1,
                y: 0,
                opacity: showActionOverlay ? 0 : 1
              }}
              style={{
                position: 'relative',
                zIndex: 'auto',
                minWidth: 0
              }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
              className={cn(
                message.type !== 'image' &&
                  (isOwnMessage
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-foreground'),
                message.type === 'image' && 'bg-transparent',
                bubbleCornerClassName,
                message.type === 'image'
                  ? 'p-0 overflow-visible'
                  : message.replyTo
                    ? 'px-0 pt-1.5 pb-3'
                    : 'px-4 py-3',
                'shadow-sm relative cursor-pointer select-none',
                !isOwnMessage &&
                  ![
                    'notification' as const,
                    'booking_selection',
                    'past_booking',
                    'simple_availability',
                  ].includes(message.type as any) &&
                  'transition-shadow hover:shadow-md'
              )}
            >
          {message.replyTo && getReplyQuoteAuthorLabel && (
            <div
              className={message.type === 'image' ? 'pt-1.5 pb-0.5' : undefined}
              style={{ paddingInline: 'calc(var(--spacing) * 2)' }}
            >
              <ChatInlineReplyQuote
                reply={message.replyTo}
                authorLabel={getReplyQuoteAuthorLabel(message.replyTo)}
                isOwnBubble={isOwnMessage}
                shellCornerClassName={getInlineReplyQuoteCornerClassName(isContinuation, isOwnMessage)}
              />
            </div>
          )}

          {/* Handle image messages */}
          {message.type === 'image' && (
            <ChatMessageImageBubbleBody
              message={message}
              bubbleCornerClassName={bubbleCornerClassName}
              eventData={eventData}
              isArtist={isArtist}
              isOwnMessage={isOwnMessage}
              onAddToTimeline={onAddToTimeline}
              setTimelineAddContextMenu={setContextMenu}
            />
          )}
          
          {/* Text content */}
          {message.content && (
            <div
              className={`text-[15px] leading-[20px] ${
                message.type === 'image'
                  ? 'px-4 py-3'
                  : message.replyTo
                    ? 'px-4 pt-0'
                    : ''
              }`}
              style={{ wordBreak: 'normal', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}
            >
              <ReactMarkdown
                components={{
                  a: ({ node, ...props }) => (
                    <a {...props} className={`${isOwnMessage ? 'text-blue-100' : 'text-blue-600'} underline`} />
                  ),
                  p: ({ node, ...props }) => (
                    <p {...props} className="m-0" style={{ wordBreak: 'normal', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }} />
                  ),
                  strong: ({ node, ...props }) => (
                    <strong {...props} className="font-semibold" />
                  )
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          )}

          {/* Like indicator pill — vertical center on bubble bottom edge; half overlaps below */}
          {message.isLiked && (
            <div
              className="pointer-events-none absolute bottom-0 left-[6px] z-[5] flex translate-y-1/2 items-center justify-center"
              aria-hidden
            >
              <div
                className="flex items-center justify-center rounded-full border-2 border-background bg-muted px-[6px] py-[3px] transition-all duration-200"
                style={{
                  animation: justLiked ? 'scale-in 0.3s ease-out' : undefined
                }}
              >
                <span className="text-[11px] leading-none font-medium">❤️</span>
              </div>
            </div>
          )}
        </motion.div>

          {/* Message status indicator - only show for LAST message in entire chat (user's own message) */}
          {isOwnMessage && isLastInChat && message.status && (
            <div className={`flex items-center justify-end mt-1.5 text-xs opacity-60 mb-1`}>
              {message.status === 'sending' && (
                <span className="text-[10px] italic">Sending...</span>
              )}
              {message.status === 'sent' && !message.isRead && (
                <span className="text-[10px] italic">Sent</span>
              )}
              {message.isRead && (
                <span className="text-[10px] italic">Seen</span>
              )}
              {message.status === 'failed' && (
                <div className="flex items-center gap-1.5 text-red-400">
                  <span className="text-[10px] italic">Failed to send</span>
                  {message.clientGeneratedId && onRetryMessage && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onRetryMessage(message.clientGeneratedId!)
                      }}
                      className="text-[10px] text-red-400 underline hover:text-red-500 active:text-red-600 transition-colors ml-1"
                    >
                      Retry
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Context menu for adding to timeline */}
      {contextMenu && onAddToTimeline && (
        <ImageContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onAddToTimeline={() => {
            onAddToTimeline(contextMenu.imageUrl)
          }}
          onClose={() => setContextMenu(null)}
        />
      )}
      </div>
      </ChatMessageSwipeReplyRow>

      {/* Instagram-style blur overlay: Copy (+ Unsend for own messages) */}
      {showActionOverlay && createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50"
            style={{ 
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              margin: 0
            }}
          >
            {/* Blur backdrop - covers entire viewport including header and input */}
            <div
              onClick={handleDismissActionOverlay}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />
            
            {/* Clone of the message above the blur - unblurred */}
            {messageSnapshot && (
              <motion.div
                initial={{ scale: 1 }}
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ 
                  duration: 0.3,
                  times: [0, 0.5, 1],
                  ease: "easeInOut"
                }}
                className={messageSnapshot.className}
                style={{
                  position: 'absolute',
                  top: messagePosition.top,
                  left: messagePosition.left,
                  width: messagePosition.width,
                  height: messagePosition.height,
                  zIndex: 60,
                  pointerEvents: 'none'
                }}
                dangerouslySetInnerHTML={{ __html: messageSnapshot.html }}
              />
            )}
            
            {/* Copy / Unsend below the message bubble */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
              className="absolute flex max-w-[min(100vw-2rem,380px)] flex-row flex-wrap gap-2"
              style={
                isOwnMessage
                  ? {
                      top: `${messagePosition.top + messagePosition.height + 16}px`,
                      right: `${window.innerWidth - (messagePosition.left + messagePosition.width)}px`,
                    }
                  : {
                      top: `${messagePosition.top + messagePosition.height + 16}px`,
                      left: `${messagePosition.left}px`,
                    }
              }
            >
              {canReply && onReplyToMessage && (
                <button
                  type="button"
                  onClick={() => {
                    onReplyToMessage(message)
                    handleDismissActionOverlay()
                  }}
                  className={cn(
                    "flex items-center justify-center gap-2 px-4 py-3 rounded-[var(--card-radius)]",
                    "bg-background text-foreground text-sm font-medium",
                    "hover:bg-muted active:scale-95",
                    "transition-all"
                  )}
                >
                  <Reply className="w-4 h-4 shrink-0" strokeWidth={2.25} />
                  <span>Reply</span>
                </button>
              )}
              {canCopy && (
                <button
                  type="button"
                  onClick={handleCopyPress}
                  className={cn(
                    "flex items-center justify-center gap-2 px-4 py-3 rounded-[var(--card-radius)]",
                    "bg-background text-foreground text-sm font-medium",
                    "hover:bg-muted active:scale-95",
                    "transition-all"
                  )}
                >
                  {copyFeedback === 'copied' ? (
                    <>
                      <Check className="w-4 h-4 shrink-0 text-green-600" strokeWidth={2.5} />
                      <span>Copied</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 shrink-0" />
                      <span>Copy</span>
                    </>
                  )}
                </button>
              )}
              {canUnsend && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className={cn(
                    "flex items-center justify-center gap-2 px-4 py-3 rounded-[var(--card-radius)]",
                    "bg-background text-red-500 text-sm font-medium",
                    "hover:bg-muted active:scale-95",
                    "transition-all",
                    "disabled:opacity-50 disabled:cursor-not-allowed"
                  )}
                >
                  <Undo2 className="w-4 h-4" />
                  <span>{isDeleting ? 'Unsending...' : 'Unsend'}</span>
                </button>
              )}
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </>
  )
}

function ChatInlineReplyQuote({
  reply,
  authorLabel,
  isOwnBubble,
  shellCornerClassName,
}: {
  reply: MessageReplyTo
  authorLabel: string
  isOwnBubble: boolean
  shellCornerClassName: string
}) {
  const handleScrollToQuotedMessage = () => {
    scrollToChatMessageByIdAndFlash(reply.messageId)
  }

  return (
    <button
      type="button"
      onClick={handleScrollToQuotedMessage}
      className={cn(
        'relative m-0 box-border block h-auto min-h-0 w-full min-w-0 cursor-pointer appearance-none overflow-hidden py-1 pl-2.5 pr-2.5 text-left transition-opacity hover:opacity-90 active:opacity-80 [-webkit-tap-highlight-color:transparent]',
        'mb-0.5',
        shellCornerClassName,
        isOwnBubble ? 'bg-black/15' : 'bg-primary/10'
      )}
    >
      <span
        className={cn(
          'pointer-events-none absolute inset-y-0 left-0 w-1',
          isOwnBubble ? 'bg-primary-foreground/50' : 'bg-primary'
        )}
        aria-hidden
      />
      <span className="relative z-[1] block pl-1">
        <span
          className={cn(
            'block text-[11px] font-semibold leading-tight',
            isOwnBubble ? 'text-primary-foreground' : 'text-primary'
          )}
        >
          {authorLabel}
        </span>
        <p
          className={cn(
            'mt-0.5 line-clamp-3 text-[12px] leading-snug opacity-90',
            isOwnBubble ? 'text-primary-foreground/90' : 'text-foreground/90'
          )}
        >
          {reply.previewText}
        </p>
      </span>
    </button>
  )
}

function ChatMessageFlashMetadataFloatingLabels({
  message,
  eventForIdentifierLookup,
  shouldShowFlashStickerLayerOnThisUploadedFrame,
}: {
  message: Message
  eventForIdentifierLookup?: { identifier?: string } | null
  shouldShowFlashStickerLayerOnThisUploadedFrame: boolean
}) {
  if (!message.metadata?.isFlash || !shouldShowFlashStickerLayerOnThisUploadedFrame) return null

  const timeFourTatsEventBrandOverlay =
    Boolean(message.metadata.eventId) &&
    eventForIdentifierLookup?.identifier === 'time4tats'

  const flashStickerGradientBackdropLayers =
    'radial-gradient(ellipse 70% 90% at 5% 50%, #5341a5 0%, transparent 70%),' +
    'radial-gradient(ellipse 65% 85% at 35% 15%, #749ed7 0%, transparent 68%),' +
    'radial-gradient(ellipse 70% 90% at 65% 85%, #fefe05 0%, transparent 68%),' +
    'radial-gradient(ellipse 65% 85% at 95% 45%, #fd2d00 0%, transparent 70%),' +
    '#2a1a4a'

  return (
    <>
      {message.metadata.minSize && (
        <div className="absolute left-2 top-2 z-[3] flex items-center rounded-full bg-background px-2 py-1 text-xs font-medium text-foreground transition-all duration-300">
          ~{message.metadata.minSize}cm
        </div>
      )}
      {message.metadata.minPrice && (
        <div className="absolute right-2 top-2 z-[3] flex items-center rounded-full bg-background px-2 py-1 text-xs font-medium text-foreground transition-all duration-300">
          {message.metadata.minPrice}€
        </div>
      )}
      {message.metadata.eventExclusive ? (
        <div
          className="absolute bottom-2 left-2 z-[3] flex items-center overflow-hidden rounded-full px-2 py-1 text-xs font-bold text-white transition-all duration-300"
          style={{ background: flashStickerGradientBackdropLayers }}
        >
          <span className="relative z-[1]">Exclusive</span>
        </div>
      ) : timeFourTatsEventBrandOverlay ? (
        <div
          className="absolute bottom-2 left-2 z-[3] flex items-center overflow-hidden rounded-full px-2 py-1 text-xs font-bold text-white transition-all duration-300"
          style={{ background: flashStickerGradientBackdropLayers }}
        >
          <span className="relative z-[1]">time4tats</span>
        </div>
      ) : null}
    </>
  )
}

function ChatMessageImageStackInteractiveRow({
  imageSrc,
  stackedImageAltTextLabel,
  stackedImageSizingClassName,
  flatPreviewUrlListIncludingThisBubble,
  indexOfThisUrlInsideFlatPreviewList,
  stopTouchMousePropagationForPortfolioLongHoldGesture,
  onLongHoldRequestTimelinePortfolioMenuFromThisRow,
  children,
}: {
  imageSrc: string
  stackedImageAltTextLabel: string
  stackedImageSizingClassName: string
  flatPreviewUrlListIncludingThisBubble: string[]
  indexOfThisUrlInsideFlatPreviewList: number
  stopTouchMousePropagationForPortfolioLongHoldGesture: boolean
  onLongHoldRequestTimelinePortfolioMenuFromThisRow: (
    tapOrHoldGestureEventSource: React.TouchEvent | React.MouseEvent
  ) => void
  children?: React.ReactNode
}) {
  const { openImagePreview } = useChatImagePreview()
  const {
    scheduleDeferredOpen: deferOpeningFullViewportImageGalleryPreview,
    cancelDeferredOpen: cancelScheduledFullViewportGalleryPreviewBecauseDoubleClick,
  } = useDeferredImageTapOpen(() =>
    openImagePreview({
      urls: flatPreviewUrlListIncludingThisBubble,
      initialIndex: indexOfThisUrlInsideFlatPreviewList,
    })
  )

  const timedPressGesturesSupportingTimelineMenuOrDeferredPreview = useLongPress({
    onLongPress: onLongHoldRequestTimelinePortfolioMenuFromThisRow,
    onClick: deferOpeningFullViewportImageGalleryPreview,
    ms: 500,
  })

  const rowOuterPointerHandlersIncludingOptionalPropagationStopLayer =
    stopTouchMousePropagationForPortfolioLongHoldGesture
      ? {
          ...timedPressGesturesSupportingTimelineMenuOrDeferredPreview,
          onTouchStart: (touchGestureStartFromRow: React.TouchEvent) => {
            timedPressGesturesSupportingTimelineMenuOrDeferredPreview.onTouchStart(touchGestureStartFromRow)
            touchGestureStartFromRow.stopPropagation()
          },
          onMouseDown: (mouseGestureStartFromRow: React.MouseEvent) => {
            timedPressGesturesSupportingTimelineMenuOrDeferredPreview.onMouseDown(mouseGestureStartFromRow)
            mouseGestureStartFromRow.stopPropagation()
          },
        }
      : timedPressGesturesSupportingTimelineMenuOrDeferredPreview

  return (
    <div
      className="relative"
      {...rowOuterPointerHandlersIncludingOptionalPropagationStopLayer}
      onDoubleClick={() =>
        cancelScheduledFullViewportGalleryPreviewBecauseDoubleClick()
      }
    >
      <ChatImageWithLoadingState
        src={imageSrc}
        alt={stackedImageAltTextLabel}
        className={stackedImageSizingClassName}
      />
      {children}
    </div>
  )
}

function ChatMessageImageBubbleBody({
  message,
  bubbleCornerClassName,
  eventData,
  isArtist,
  isOwnMessage,
  onAddToTimeline,
  setTimelineAddContextMenu,
}: {
  message: Message
  bubbleCornerClassName: string
  eventData?: { identifier?: string } | null
  isArtist: boolean
  isOwnMessage: boolean
  onAddToTimeline?: (imageUrl: string) => void
  setTimelineAddContextMenu: React.Dispatch<
    React.SetStateAction<{ x: number; y: number; imageUrl: string } | null>
  >
}) {
  const orderedPreviewUrlsForThisMessageBubble = getFlatImageUrlsFromMessage(message)
  const artistPortfolioLongHoldGestureShouldStopBubblePropagation =
    isArtist && !isOwnMessage && !!onAddToTimeline

  const openTimelinePortfolioContextMenuAnchoredNearImageInteraction = (
    tapOrHoldEvent: React.TouchEvent | React.MouseEvent,
    imageUrlChosenForPortfolioAttachment: string
  ) => {
    if (!isArtist || isOwnMessage || !onAddToTimeline) return
    tapOrHoldEvent.preventDefault()
    const boundingRectAroundInteractionTargetElement = (
      tapOrHoldEvent.target as HTMLElement
    ).getBoundingClientRect()
    setTimelineAddContextMenu({
      x:
        boundingRectAroundInteractionTargetElement.left +
        boundingRectAroundInteractionTargetElement.width / 2,
      y:
        boundingRectAroundInteractionTargetElement.top +
        boundingRectAroundInteractionTargetElement.height / 2,
      imageUrl: imageUrlChosenForPortfolioAttachment,
    })
  }

  return (
    <div className="relative z-[1]">
      {message.imageUrl && !message.imageUrls && (
        <ChatMessageImageStackInteractiveRow
          imageSrc={message.imageUrl}
          stackedImageAltTextLabel="Chat image"
          stackedImageSizingClassName={cn(
            'relative z-[1] max-h-[40vh] w-full object-contain',
            bubbleCornerClassName
          )}
          flatPreviewUrlListIncludingThisBubble={orderedPreviewUrlsForThisMessageBubble}
          indexOfThisUrlInsideFlatPreviewList={0}
          stopTouchMousePropagationForPortfolioLongHoldGesture={
            artistPortfolioLongHoldGestureShouldStopBubblePropagation
          }
          onLongHoldRequestTimelinePortfolioMenuFromThisRow={holdGestureEvent =>
            openTimelinePortfolioContextMenuAnchoredNearImageInteraction(holdGestureEvent, message.imageUrl!)
          }
        >
          <ChatMessageFlashMetadataFloatingLabels
            message={message}
            eventForIdentifierLookup={eventData}
            shouldShowFlashStickerLayerOnThisUploadedFrame={true}
          />
        </ChatMessageImageStackInteractiveRow>
      )}

      {message.imageUrls && message.imageUrls.length > 0 && (
        <div
          className={cn('flex w-full flex-col gap-1 overflow-hidden', bubbleCornerClassName)}
        >
          {message.imageUrls.map((stackedGalleryImageSrc, stackedGalleryRowIndex) => (
            <ChatMessageImageStackInteractiveRow
              key={`${stackedGalleryImageSrc}-${stackedGalleryRowIndex}`}
              imageSrc={stackedGalleryImageSrc}
              stackedImageAltTextLabel={`Chat image ${stackedGalleryRowIndex + 1}`}
              stackedImageSizingClassName="max-h-[40vh] w-full max-w-full rounded-none object-contain"
              flatPreviewUrlListIncludingThisBubble={orderedPreviewUrlsForThisMessageBubble}
              indexOfThisUrlInsideFlatPreviewList={stackedGalleryRowIndex}
              stopTouchMousePropagationForPortfolioLongHoldGesture={
                artistPortfolioLongHoldGestureShouldStopBubblePropagation
              }
              onLongHoldRequestTimelinePortfolioMenuFromThisRow={holdGestureEvent =>
                openTimelinePortfolioContextMenuAnchoredNearImageInteraction(
                  holdGestureEvent,
                  stackedGalleryImageSrc
                )
              }
            >
              <ChatMessageFlashMetadataFloatingLabels
                message={message}
                eventForIdentifierLookup={eventData}
                shouldShowFlashStickerLayerOnThisUploadedFrame={stackedGalleryRowIndex === 0}
              />
            </ChatMessageImageStackInteractiveRow>
          ))}
        </div>
      )}
      <div className="pointer-events-none absolute inset-0 z-[2] bg-primary/5" />
    </div>
  )
}
